import { ObjectId } from "@novorender/data-js-api";
import { createContext, Dispatch, MutableRefObject } from "react";

import { VecRGBA } from "utils/color";

import { toIdArr, toIdObj } from "../utils";

// Highlighted/hidden/objectgroups may end up having huge (1M+) collections of objectIds and receive a lot of back-to-back state updates.
// Keeping this state in the redux store ended up slowing down the app too much so we keep it in React contexts instead for now.

export const initialState = {
    ids: {} as Record<ObjectId, true | undefined>,
    idArr: [] as ObjectId[],
    color: [1, 0, 0, 1] as VecRGBA,
};

export type State = typeof initialState;

enum ActionTypes {
    Add,
    Remove,
    SetIds,
    SetColor,
    ResetColor,
    Set,
    RemoveAndAdd,
}

function add(ids: ObjectId[]) {
    return {
        type: ActionTypes.Add as const,
        ids,
    };
}

function remove(ids: Iterable<ObjectId>) {
    return {
        type: ActionTypes.Remove as const,
        ids,
    };
}

function setIds(ids: ObjectId[]) {
    return {
        type: ActionTypes.SetIds as const,
        ids,
    };
}

function setColor(color: State["color"]) {
    return {
        type: ActionTypes.SetColor as const,
        color,
    };
}

function resetColor() {
    return {
        type: ActionTypes.ResetColor as const,
    };
}

function set(payload: { color: State["color"]; ids: ObjectId[] }) {
    return {
        type: ActionTypes.Set as const,
        payload,
    };
}

function removeAndAdd(payload: { toRemove: Iterable<ObjectId>; toAdd: ObjectId[] }) {
    return {
        type: ActionTypes.RemoveAndAdd as const,
        payload,
    };
}

export const actions = { add, remove, setIds, setColor, resetColor, set, removeAndAdd };

type Actions = ReturnType<(typeof actions)[keyof typeof actions]>;
export type DispatchHighlighted = Dispatch<Actions>;
export type LazyState = MutableRefObject<State>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StateContext = createContext<State>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyStateContext = createContext<LazyState>(undefined as any);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DispatchContext = createContext<DispatchHighlighted>(undefined as any);

export function reducer(state: State, action: Actions): State {
    switch (action.type) {
        case ActionTypes.Add: {
            const ids = { ...state.ids, ...toIdObj(action.ids) };

            return {
                ids,
                idArr: toIdArr(ids),
                color: state.color,
            };
        }
        case ActionTypes.Remove: {
            for (const id of action.ids) {
                delete state.ids[id];
            }

            return {
                color: state.color,
                ids: { ...state.ids },
                idArr: toIdArr(state.ids),
            };
        }
        case ActionTypes.SetIds: {
            return {
                color: state.color,
                ids: toIdObj(action.ids),
                idArr: action.ids,
            };
        }
        case ActionTypes.SetColor: {
            return {
                color: action.color,
                ids: state.ids,
                idArr: state.idArr,
            };
        }
        case ActionTypes.ResetColor: {
            return {
                color: initialState.color,
                ids: state.ids,
                idArr: state.idArr,
            };
        }
        case ActionTypes.Set: {
            return {
                color: action.payload.color,
                ids: toIdObj(action.payload.ids),
                idArr: action.payload.ids,
            };
        }
        case ActionTypes.RemoveAndAdd: {
            // Optimize for performance: avoid multiple object spreads for large arrays
            // Materialize toRemove once as a Set to avoid consuming iterators multiple times
            const toRemoveSet = new Set(action.payload.toRemove);

            const stateIdCount = state.idArr.length;
            const toAddCount = action.payload.toAdd.length;
            const shouldStartFresh = toAddCount >= stateIdCount - toRemoveSet.size;
            let ids: Record<ObjectId, true | undefined>;

            if (shouldStartFresh) {
                // Build new object from scratch - more efficient for large adds
                ids = toIdObj(action.payload.toAdd);

                // Then add back any existing IDs that weren't in toRemove
                for (const key in state.ids) {
                    const id = Number(key);
                    if (!toRemoveSet.has(id) && !ids[id]) {
                        ids[id] = true;
                    }
                }
            } else {
                // Traditional approach for smaller changes
                ids = { ...state.ids };

                for (const id of toRemoveSet) {
                    if (ids[id]) {
                        delete ids[id];
                    }
                }

                // Add new IDs
                for (const id of action.payload.toAdd) {
                    ids[id] = true;
                }
            }

            const idArr = toIdArr(ids);

            return {
                color: state.color,
                ids,
                idArr,
            };
        }
        default: {
            throw new Error(`Unhandled action type`);
        }
    }
}
